import API from 'constants/API';

import {AxiosResponse} from 'axios';
import {assignPubEditorDTO} from 'typescript/dto/assignPubEditorDTO';
import {CreatePubBeerDTO} from 'typescript/dto/createPubBeerDto';
import {CreatePubEditorDTO} from 'typescript/dto/createPubEditorDTO';
import {EditPubBeerDTO} from 'typescript/dto/editPubBeerDTO';
import {IAdminPubBeersDTO} from 'typescript/dto/getAdminPubBeersDTO';
import {IGetPageEditorPubsDTO} from 'typescript/dto/getPageEditorPubsDTO';
import {getPubBeersDto} from 'typescript/dto/getPubBeersDto';
import {CreatePubDTO, GetPubsDTO} from 'typescript/dto/pubsDto';
import {RemovePubBeerDTO} from 'typescript/dto/removePubBeerDto';
import {ReorderPubBeerDTO} from 'typescript/dto/reorderPubBeerDto';
import {ToggleBeerOnTapDto} from 'typescript/dto/toggleBeerOnTapDto';
import ApiData from 'typescript/interfaces/ApiData';
import {AssignedEditorPubs} from 'typescript/models/AssignedEditorPubs';
import {ManagerDashboardData} from 'typescript/models/ManagerDashboardData';
import PageEditor from 'typescript/models/PageEditor';
import {PubBeer} from 'typescript/models/PubBeer';
import PubPage from 'typescript/models/PubPage';
import {queryBuilder} from 'utils/helpers/queryBuilder';
import httpClient from 'utils/httpClient/httpClient';

export const getPubsData = (search: GetPubsDTO): Promise<AxiosResponse<ApiData<PubPage[]>>> => {
  const {perPage, name, column, direction} = search;

  const baseQuery = queryBuilder({
    baseQuery: `${API.PUB_SERVICE.BASE}`,
    queryObject: {per_page: perPage, 'filter[name]': name},
  });

  let optionalQueryParams = '';
  if (column && direction) {
    optionalQueryParams = queryBuilder({
      baseQuery,
      queryObject: {'filter_sort[sort_column]': column, 'filter_sort[sort_direction]': direction},
    });
  }

  return httpClient.get(optionalQueryParams || baseQuery);
};

export const getManagedPubs = (dto: IGetPageEditorPubsDTO): Promise<AxiosResponse<ApiData<AssignedEditorPubs[]>>> => {
  const query = queryBuilder({
    baseQuery: `${API.AUTH_SERVICE.BASE}/${dto.userId}/assigned_editors`,
    queryObject: {page: dto.page, per_page: dto.per_page},
  });
  return httpClient.get(query);
};

export const createNewPub = (newPub: CreatePubDTO): Promise<AxiosResponse<ApiData<PubPage>>> => {
  const data = {pub: newPub};

  return httpClient.post(API.PUB_SERVICE.BASE, data);
};

export const getPubBeersData = (dto: getPubBeersDto): Promise<AxiosResponse<ApiData<PubBeer[]>>> => {
  const {pubId, perPage, page} = dto;

  const query = queryBuilder({
    baseQuery: `${API.PUB_SERVICE.BASE}/${pubId}/pub_beers`,
    queryObject: {per_page: perPage, page},
  });
  return httpClient.get(query);
};

export const getSinglePubData = (id: string): Promise<AxiosResponse<ApiData<PubPage>>> => {
  return httpClient.get(`${API.PUB_SERVICE.BASE}/${id}`);
};

export const updateSinglePubData = (id: string, updatedPub: CreatePubDTO): Promise<AxiosResponse<ApiData<PubPage>>> => {
  const data = {pub: updatedPub};

  return httpClient.put(`${API.PUB_SERVICE.BASE}/${id}`, data);
};

export const getPubAssignedEditors = (dto: {
  pubId: string;
  column?: string;
  direction?: 'asc' | 'desc';
}): Promise<AxiosResponse<ApiData<PageEditor[]>>> => {
  const query = queryBuilder({
    baseQuery: `${API.PUB_SERVICE.BASE}/${dto.pubId}/assigned_editors`,
    queryObject: {'filter_sort[sort_column]': dto.column, 'filter_sort[sort_direction]': dto.direction, per_page: 10},
  });

  return httpClient.get(query);
};

export const unassignPubEditor = (pubId: string, editorId: string): Promise<AxiosResponse<ApiData<string>>> => {
  return httpClient.delete(`${API.PUB_SERVICE.BASE}/${pubId}/assigned_editors/${editorId}`);
};

export const createPubBeer = ({
  dto,
  pubId,
}: {
  dto: CreatePubBeerDTO;
  pubId: number;
}): Promise<AxiosResponse<ApiData<PubBeer>>> => {
  return httpClient.post(`${API.PUB_SERVICE.BASE}/${pubId}/pub_beers`, dto);
};

export const editPubBeer = ({
  dto,
  pubId,
}: {
  dto: EditPubBeerDTO;
  pubId: string;
}): Promise<AxiosResponse<ApiData<PubBeer>>> => {
  return httpClient.put(`${API.PUB_SERVICE.BASE}/${pubId}/pub_beers/${dto.pub_beer_id}`, {
    beer_variants_attributes: dto.beer_variants_attributes,
  });
};

export const deletePubBeer = (data: RemovePubBeerDTO): Promise<AxiosResponse<ApiData<string>>> => {
  const {pubId, beerId} = data;

  return httpClient.delete(`${API.PUB_SERVICE.BASE}/${pubId}/pub_beers/${beerId}`);
};

export const reorderPubBeer = (data: ReorderPubBeerDTO): Promise<AxiosResponse<ApiData<PubBeer>>> => {
  const {pubId, beerId, position} = data;

  return httpClient.put(`${API.PUB_SERVICE.BASE}/${pubId}/pub_beers/${beerId}/position`, {position});
};

export const createPubsAssignedEditor = (dto: assignPubEditorDTO): Promise<AxiosResponse<ApiData<PageEditor[]>>> => {
  return httpClient.post(`${API.PUB_SERVICE.BASE}/${dto.pubId}/assigned_editors`, {
    assigned_editor: {pub_id: dto.pubId, user_id: dto.userId},
  });
};

export const createPubEditor = ({pubId, userId}: CreatePubEditorDTO): Promise<AxiosResponse<ApiData<PageEditor>>> => {
  const data = {assigned_editor: {user_id: userId, pub_id: pubId}};

  return httpClient.post(`${API.PUB_SERVICE.BASE}/${pubId}/assigned_editors`, data);
};

export const getPubDashboardData = (id: string): Promise<AxiosResponse<ApiData<ManagerDashboardData>>> => {
  return httpClient.get(`${API.PUB_SERVICE.BASE}/${id}/dashboard_counter`);
};

export const addBeerOnTap = (dto: {pubId: number; pubBeerId: number}): Promise<AxiosResponse<ApiData<PubBeer>>> => {
  return httpClient.post(`${API.PUB_SERVICE.BASE}/${dto.pubId}/beer_on_taps`, {
    beer_on_tap: {pub_beer_id: [+dto.pubBeerId]},
  });
};

export const removeBeerFromTap = (dto: {id: number; pubId: number}): Promise<AxiosResponse<ApiData<PubBeer>>> => {
  return httpClient.delete(`${API.PUB_SERVICE.BASE}/${dto.pubId}/beer_on_taps/${dto.id}`);
};

export const toggleBeerOnTap = (dto: ToggleBeerOnTapDto): Promise<AxiosResponse<ApiData<PubBeer>>> => {
  const query = queryBuilder({
    baseQuery: `${API.PUB_SERVICE.BASE}/${dto.pubId}/pub_beers/${dto.pubBeerId}`,
    queryObject: {
      'pub_beer[on_tap]': dto.on_tap.toString(),
    },
  });

  return httpClient.put(query);
};

export const getAdminPubBeers = (dto: IAdminPubBeersDTO): Promise<AxiosResponse<ApiData<PubBeer[]>>> => {
  const {perPage, pubId, column, direction} = dto;

  const query = queryBuilder({
    baseQuery: `/admin${API.PUB_SERVICE.BASE}/${pubId}/pub_beers`,
    queryObject: {per_page: perPage, 'filter_sort[sort_column]': column, 'filter_sort[sort_direction]': direction},
  });

  return httpClient.get(query);
};
